import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Home, Search, MapPin, FileCheck, Shield, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Header } from '@/components/layout/Header';
import { TownNavBar } from '@/components/layout/TownNavBar';
import { AreaNavBar } from '@/components/layout/AreaNavBar';
import { Footer } from '@/components/layout/Footer';
import { RealtorCard } from '@/components/realtor/RealtorCard';
import { OfferLeadForm } from '@/components/forms/OfferLeadForm';
import { supabase } from '@/integrations/supabase/client';
import { gaEvents, useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { PublicThemeProvider } from '@/components/theme/ThemeProvider';

// Generate structured data for SEO
const generateStructuredData = (site: any, realtor: any, canonicalUrl: string | null, pageContent: any) => {
  const realtorName = realtor?.first_name && realtor?.last_name
    ? `${realtor.first_name} ${realtor.last_name}`
    : null;
  
  const structuredData: any[] = [];

  // Service schema
  structuredData.push({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `Home Buyer Services in ${site.location_served}`,
    "description": pageContent?.seo_description || `Expert home buyer services in ${site.location_served}. Find your dream home with professional real estate guidance.`,
    "provider": {
      "@type": "RealEstateAgent",
      "name": realtorName || site.name,
      "image": realtor?.photo_url || undefined,
      "telephone": realtor?.phone || undefined,
      "areaServed": {
        "@type": "Place",
        "name": site.location_served
      }
    },
    "areaServed": {
      "@type": "Place",
      "name": site.location_served
    },
    "serviceType": "Real Estate Buyer Agent Services"
  });

  // BreadcrumbList schema
  structuredData.push({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": canonicalUrl?.replace('/buyers', '') || undefined
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Buyer Services",
        "item": canonicalUrl || undefined
      }
    ]
  });

  // WebPage schema
  structuredData.push({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageContent?.seo_title || `Home Buyer Services | ${site.name}`,
    "description": pageContent?.seo_description,
    "url": canonicalUrl,
    "isPartOf": {
      "@type": "WebSite",
      "name": site.name,
      "url": canonicalUrl?.replace('/buyers', '')
    }
  });

  return structuredData;
};

interface SiteData {
  id: string;
  name: string;
  location_served: string;
  theme: string;
  favicon_url: string | null;
  tenant_id: string | null;
  canonical_base_url: string | null;
  header_font: string | null;
  enable_market_value_report?: boolean;
  enable_community_guide?: boolean;
  enable_mortgage_info?: boolean;
  realtor_assigned?: boolean;
}

interface TownData {
  id: string;
  name: string;
  slug: string;
}

interface RealtorData {
  id?: string;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  license_numbers: string | null;
  about_me: string | null;
  logo_url: string | null;
  title_preference?: string | null;
  ad_image_url?: string | null;
}

interface Benefit {
  title: string;
  description: string;
}

interface PageContent {
  headline: string;
  subheadline: string;
  intro_text: string;
  benefits: Benefit[];
  cta_text: string;
  cta_button_text: string;
  seo_title: string;
  seo_description: string;
  og_title: string;
  og_description: string;
}

const defaultBenefits = [
  { title: 'Property Search', description: 'Access exclusive listings and get alerts on new properties that match your criteria before they hit the market.' },
  { title: 'Neighborhood Expertise', description: 'Deep local knowledge to help you find the perfect community for your lifestyle.' },
  { title: 'Offer Negotiation', description: 'Expert negotiation to ensure you get the best possible price and terms on your new home.' },
  { title: 'Transaction Support', description: 'Guidance through every step of the buying process, from initial search to closing day.' },
];

const benefitIcons = [Search, MapPin, FileCheck, Shield];

export default function BuyersPage() {
  const [site, setSite] = useState<SiteData | null>(null);
  const [towns, setTowns] = useState<TownData[]>([]);
  const [areas, setAreas] = useState<{ id: string; name: string; slug: string }[]>([]);
  const [realtor, setRealtor] = useState<RealtorData | null>(null);
  const [pageContent, setPageContent] = useState<PageContent | null>(null);
  const [loading, setLoading] = useState(true);

  useGoogleAnalytics(site?.tenant_id || null);

  useEffect(() => {
    fetchSiteData();
  }, []);

  useEffect(() => {
    if (site && !loading) {
      gaEvents.viewOffer('buyer_services', '/buyers');
    }
  }, [site, loading]);

  const fetchSiteData = async () => {
    setLoading(true);
    try {
      const hostname = window.location.hostname;
      const isDevelopment = hostname === 'localhost' || hostname.includes('lovable.app');
      
      let siteData: SiteData | null = null;

      if (!isDevelopment) {
        const hostWithoutWww = hostname.replace(/^www\./, '');
        const { data } = await supabase
          .from('sites_public' as any)
          .select('id, name, location_served, theme, favicon_url, tenant_id, canonical_base_url, header_font, enable_market_value_report, enable_community_guide, enable_mortgage_info, realtor_assigned')
          .eq('status', 'active')
          .or(`primary_domain.eq.${hostname},primary_domain.eq.${hostWithoutWww}`)
          .limit(1)
          .maybeSingle();
        siteData = data as unknown as SiteData;
      }

      if (!siteData && isDevelopment) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: siteAdmin } = await supabase
            .from('site_admins')
            .select('site_id')
            .eq('user_id', user.id)
            .single();

          if (siteAdmin) {
            const { data } = await supabase
              .from('sites_public' as any)
              .select('id, name, location_served, theme, favicon_url, tenant_id, canonical_base_url, header_font, enable_market_value_report, enable_community_guide, enable_mortgage_info, realtor_assigned')
              .eq('id', siteAdmin.site_id)
              .maybeSingle();
            siteData = data as unknown as SiteData;
          }
        }
      }

      if (!siteData) {
        const { data } = await supabase
          .from('sites_public' as any)
          .select('id, name, location_served, theme, favicon_url, tenant_id, canonical_base_url, header_font, enable_market_value_report, enable_community_guide, enable_mortgage_info, realtor_assigned')
          .eq('status', 'active')
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        siteData = data as unknown as SiteData;
      }

      if (siteData) {
        setSite(siteData);

        // Fetch page content from database
        const { data: pageData } = await supabase
          .from('site_pages' as any)
          .select('*')
          .eq('site_id', siteData.id)
          .eq('page_type', 'buyer')
          .eq('is_published', true)
          .maybeSingle();
        
        const row = pageData as any;
        if (row) {
          setPageContent({
            headline: row.headline || '',
            subheadline: row.subheadline || '',
            intro_text: row.intro_text || '',
            benefits: Array.isArray(row.benefits_json) ? row.benefits_json : [],
            cta_text: row.cta_text || '',
            cta_button_text: row.cta_button_text || 'Start My Home Search',
            seo_title: row.seo_title || '',
            seo_description: row.seo_description || '',
            og_title: row.og_title || '',
            og_description: row.og_description || '',
          });
        }

        const { data: townsData } = await supabase
          .from('towns')
          .select('id, name, slug')
          .eq('site_id', siteData.id)
          .eq('is_published', true)
          .order('name');
        setTowns(townsData || []);
        
        if (townsData && townsData.length === 1) {
          const { data: areasData } = await supabase
            .from('areas')
            .select('id, name, slug')
            .eq('town_id', townsData[0].id)
            .eq('is_published', true)
            .order('name');
          
          if (areasData) setAreas(areasData);
        }

        const { data: realtorData } = await supabase
          .from('realtor_profiles')
          .select('id, first_name, last_name, photo_url, phone, city, state, license_numbers, about_me, logo_url, title_preference, ad_image_url')
          .eq('site_id', siteData.id)
          .single();
        setRealtor(realtorData);
      }
    } catch (error) {
      console.error('Error fetching site data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading...</title>
          <meta name="description" content="" />
          <meta property="og:title" content="" />
          <meta property="og:description" content="" />
          <meta name="twitter:title" content="" />
          <meta name="twitter:description" content="" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </>
    );
  }

  if (!site) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Site not found</p>
      </div>
    );
  }

  const showRealtorContent = site.realtor_assigned !== false;
  const realtorName = showRealtorContent && realtor?.first_name && realtor?.last_name
    ? `${realtor.first_name} ${realtor.last_name}`
    : null;
  const titleLabel = realtor?.title_preference === 'realtor' ? 'realtor' : 'agent';

  const canonicalUrl = site.canonical_base_url 
    ? `${site.canonical_base_url}/buyers`
    : null;

  const navTowns = towns.map(t => ({ id: t.id, name: t.name, slug: t.slug }));

  // Use database content or fallback to defaults
  const headline = pageContent?.headline || `Find Your Dream Home in ${site.location_served}`;
  const subheadline = pageContent?.subheadline || `Expert guidance from search to closing. Let ${realtorName || `your local ${titleLabel}`} help you navigate the home buying journey.`;
  const introText = pageContent?.intro_text || `Buying a home is one of the biggest decisions you'll make. Whether you're a first-time buyer or seasoned investor, having an experienced ${titleLabel} by your side makes all the difference.`;
  const benefits = pageContent?.benefits && pageContent.benefits.length > 0 
    ? pageContent.benefits.map((b, i) => ({ ...b, icon: benefitIcons[i % benefitIcons.length] }))
    : defaultBenefits.map((b, i) => ({ ...b, icon: benefitIcons[i], description: b.description.replace('to help you find', `of ${site.location_served} to help you find`) }));
  const ctaText = pageContent?.cta_text || `Connect with ${realtorName || `a local ${titleLabel}`} who knows ${site.location_served} inside and out. Get personalized property recommendations and expert guidance throughout your buying journey - at no cost to you until you find your perfect home.`;
  const ctaButtonText = pageContent?.cta_button_text || 'Start My Home Search';
  const seoTitle = pageContent?.seo_title || `Home Buyer Services | ${site.name}`;
  const seoDescription = pageContent?.seo_description || `Looking to buy a home in ${site.location_served}? ${realtorName || 'Your local agent'} provides expert guidance for first-time buyers and experienced investors alike.`;

  // Generate structured data for SEO
  const structuredData = generateStructuredData(site, realtor, canonicalUrl, pageContent);

  return (
    <PublicThemeProvider themeId={site.theme} headerFontId={site.header_font}>
      <div className="min-h-screen bg-background flex flex-col">
        <Helmet>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDescription} />
          {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
          
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content={site.name} />
          <meta property="og:title" content={pageContent?.og_title || seoTitle} />
          <meta property="og:description" content={pageContent?.og_description || seoDescription} />
          {/* og:url and og:image are injected by Cloudflare worker */}
          
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={pageContent?.og_title || seoTitle} />
          <meta name="twitter:description" content={pageContent?.og_description || seoDescription} />
          
          <meta name="robots" content="index, follow" />
          
          {/* Structured Data for SEO */}
          {structuredData.map((data, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(data)}
            </script>
          ))}
        </Helmet>

        <Header 
          siteName={site.name} 
          faviconUrl={site.favicon_url}
          logoUrl={realtor?.logo_url}
          showAdmin={false}
          enableMarketValueReport={site.enable_market_value_report !== false}
          enableCommunityGuide={site.enable_community_guide !== false}
          enableMortgageInfo={site.enable_mortgage_info !== false}
        />

        <TownNavBar 
          towns={navTowns}
          currentTownSlug=""
          enableMarketValueReport={site.enable_market_value_report !== false}
          enableMortgageInfo={site.enable_mortgage_info !== false}
          realtorFirstName={realtor?.first_name}
          themeId={site.theme}
        />
        
        {towns.length === 1 && areas.length > 0 && (
          <AreaNavBar areas={areas} townSlug={towns[0].slug} />
        )}

        <main className="flex-1">
          {/* Hero Section */}
          <section className="relative py-16 md:py-24" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)' }}>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
              <h1 className="text-3xl md:text-5xl font-display font-bold mb-4 text-white">
                {headline}
              </h1>
              <p className="text-xl md:text-2xl max-w-3xl mx-auto text-white/90">
                {subheadline}
              </p>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-12 md:py-16">
            <div className="container px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
                {/* Left: Content - 70% */}
                <div className="lg:col-span-7 space-y-8">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4">
                      What You Get When You Work With {realtorName || 'Us'}
                    </h2>
                    <p className="text-muted-foreground mb-8">
                      {introText}
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    {benefits.map((benefit, index) => {
                      const IconComponent = 'icon' in benefit ? benefit.icon : benefitIcons[index % benefitIcons.length];
                      return (
                        <Card key={index} className="bg-muted/50">
                          <CardContent className="p-6">
                            <div className="flex items-start gap-4">
                              <div className="p-2 rounded-full bg-accent/20 shrink-0">
                                <IconComponent className="h-6 w-6 text-accent-foreground" />
                              </div>
                              <div>
                                <h3 className="font-semibold mb-1">{benefit.title}</h3>
                                <p className="text-sm text-muted-foreground">{benefit.description}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  <div className="bg-secondary/30 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Users className="h-5 w-5 text-accent-foreground" />
                      Ready to Start Your Home Search?
                    </h3>
                    <p className="text-muted-foreground">
                      {ctaText}
                    </p>
                  </div>

                  {/* Form */}
                  <Card className="p-6 lg:p-8">
                    <h3 className="text-2xl font-display font-bold mb-2 text-center">
                      Let's Find Your Perfect Home
                    </h3>
                    <p className="text-muted-foreground text-center mb-6">
                      Tell {realtorName || 'us'} what you're looking for and {realtorName ? "I'll" : "we'll"} be in touch to start your personalized home search.
                    </p>
                    <OfferLeadForm 
                      siteId={site.id}
                      requestType="buyer_inquiry"
                      pagePath="/buyers"
                      submitButtonText={ctaButtonText}
                      siteDomain={window.location.hostname}
                    />
                  </Card>
                </div>

                {/* Right: Sidebar - 30% */}
                <aside className="lg:col-span-3 space-y-6">
                  {showRealtorContent && realtor && (
                    <RealtorCard realtor={realtor} adTypeFilter="buyer" />
                  )}
                </aside>
              </div>
            </div>
          </section>
        </main>

        <Footer 
          siteName={site.name} 
          locationServed={site.location_served}
          towns={navTowns}
          areas={areas}
          realtor={showRealtorContent ? realtor : null}
          enableBuyers={true}
          enableSellers={true}
          enableMortgageInfo={site.enable_mortgage_info !== false}
        />
      </div>
    </PublicThemeProvider>
  );
}
